import { downloadXml } from "./firmwareKlydosXml";

// The raw artist/user fields we read off /admin/users/:id (the TS User type is a
// narrower auth shape, so we describe the artist-profile fields we actually use).
export type ArtistUser = {
  id: string;
  name?: string;
  email?: string;
  description?: string;
  photoUrl?: string;
  country?: string;
  socialMedia?: { link: string; network: string }[];
};

// Last path segment of a URL (its "<name>.<ext>" basename), query stripped.
export const urlBaseName = (url: string): string => {
  const last = (url || "").split("/").pop() ?? "";
  return last.split("?")[0];
};

// socialMedia.link is stored as a full URL; the cache wants just the handle
// (last path segment), and the network lower-cased. Already-bare handles pass
// through unchanged.
const socialHandle = (link: string): string => {
  const trimmed = (link || "").trim().replace(/\/+$/, "");
  if (!trimmed) return "";
  return trimmed.includes("/") ? (trimmed.split("/").pop() ?? "") : trimmed;
};

const escapeXml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const artistToCacheObject = (u: ArtistUser) => {
  const photo = u.photoUrl ? urlBaseName(u.photoUrl) : "";
  const obj: Record<string, unknown> = {
    id: u.id,
    name: u.name ?? "",
  };
  // `about` is omitted entirely when empty (matches the reference cache).
  if (u.description && u.description.trim()) obj.about = u.description;
  obj.email = u.email ?? "";
  obj.photo = photo;
  obj.country = u.country ?? "";
  obj.socialMedia = (u.socialMedia ?? []).map((s) => ({
    link: socialHandle(s.link),
    network: (s.network ?? "").toLowerCase(),
  }));
  // animated = the profile photo is a gif.
  obj.animated = photo.toLowerCase().endsWith(".gif");
  obj.local = true;
  obj.oem = true;
  return obj;
};

export const buildArtistsXml = (artists: ArtistUser[]): string => {
  const entries = artists
    .map((a) => {
      const json = JSON.stringify(artistToCacheObject(a));
      return `    <string name="${escapeXml(a.id)}">${escapeXml(json)}</string>`;
    })
    .join("\n");
  return `<?xml version='1.0' encoding='utf-8' standalone='yes' ?>\n<map>\n${entries}\n</map>`;
};

export { downloadXml };
